// src/pages/Admin/Dashboard/Events/components/EventsGrid.jsx

import React from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaEye,
  FaImage,
  FaLock,
  FaLockOpen,
  FaPen,
  FaPowerOff,
  FaTimes,
  FaTrashAlt,
  FaUserPlus,
  FaUserSlash,
} from "react-icons/fa";

import {
  formatEventDateShort,
  getMediaUrl,
  getRegistrationsCount,
  truncateText,
} from "../utils/eventsUtils";

function IconButton({ title, icon: Icon, onClick, tone = "slate" }) {
  const tones = {
    slate:
      "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-950 dark:border-slate-700 dark:text-slate-300 dark:hover:text-white",
    blue:
      "border-blue-100 text-blue-600 hover:border-blue-200 hover:bg-blue-50 dark:border-blue-500/20 dark:text-blue-300 dark:hover:bg-blue-500/10",
    orange:
      "border-orange-100 text-orange-600 hover:border-orange-200 hover:bg-orange-50 dark:border-orange-500/20 dark:text-orange-300 dark:hover:bg-orange-500/10",
    rose:
      "border-rose-100 text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10",
    emerald:
      "border-emerald-100 text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 dark:border-emerald-500/20 dark:text-emerald-300 dark:hover:bg-emerald-500/10",
  };

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`grid h-10 w-10 place-items-center rounded-xl border bg-white text-sm transition dark:bg-slate-950 ${tones[tone]}`}
    >
      <Icon />
    </button>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function RegisterBadge({ open }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
        open
          ? "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
          : "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
      }`}
    >
      {open ? <FaLockOpen /> : <FaLock />}
      {open ? "Inscriptions ouvertes" : "Inscriptions fermées"}
    </span>
  );
}

function EventSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-48 animate-pulse bg-slate-100 dark:bg-slate-800" />
      <div className="p-5">
        <div className="h-5 w-40 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
        <div className="mt-4 h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}

export default function EventsGrid({
  loading,
  events = [],
  deleteCandidateId,
  onDetails,
  onEdit,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  onToggleActive,
  onToggleRegister,
}) {
  if (loading) {
    return (
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <EventSkeleton key={index} />
        ))}
      </section>
    );
  }

  if (!events.length) {
    return (
      <section className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-3xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <FaUserSlash className="text-2xl" />
        </div>

        <h3 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
          Aucun événement trouvé
        </h3>

        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Modifiez votre recherche ou créez un nouvel événement.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
      {events.map((event) => {
        const imageUrl = getMediaUrl(event.photo_url);
        const isDeleting = deleteCandidateId === event.id;
        const registrationsCount = getRegistrationsCount(event);

        return (
          <article
            key={event.id}
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/80 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
          >
            <div className="relative h-56 overflow-hidden bg-slate-100 dark:bg-slate-800">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={event.titre}
                  className="h-full w-full object-cover transition duration-500 hover:scale-105"
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <FaImage className="text-4xl" />
                  <p className="mt-3 text-sm font-medium">
                    Aucune image renseignée
                  </p>
                </div>
              )}

              <div className="absolute left-4 top-4 flex flex-wrap gap-2">
                <StatusBadge active={event.is_active} />
              </div>

              <div className="absolute bottom-4 left-4 rounded-2xl bg-slate-950/80 px-3 py-2 text-xs font-bold text-white backdrop-blur">
                <span className="inline-flex items-center gap-2">
                  <FaCalendarAlt />
                  {formatEventDateShort(event.start)}
                </span>
              </div>
            </div>

            <div className="p-5">
              <div className="flex flex-wrap items-center gap-2">
                <RegisterBadge open={event.can_register} />
              </div>

              <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                {event.titre}
              </h3>

              <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                {event.type_event && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                    {event.type_event}
                  </span>
                )}

                {event.cate_event && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                    {event.cate_event}
                  </span>
                )}

                {event.level_event && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                    {event.level_event}
                  </span>
                )}
              </div>

              <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-500 dark:text-slate-400">
                {truncateText(event.description || "Description non renseignée.")}
              </p>

              <div className="mt-4 grid gap-2 rounded-2xl bg-slate-50 p-3 text-sm dark:bg-slate-950">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <FaClock className="text-slate-400" />
                  <span>{event.horaire_event || "Horaires non renseignés"}</span>
                </div>

                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <FaUserPlus className="text-slate-400" />
                  <span>
                    {registrationsCount} inscrit
                    {registrationsCount > 1 ? "s" : ""}
                    {event.max_registrations
                      ? ` / ${event.max_registrations}`
                      : ""}
                  </span>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-end gap-2">
                {isDeleting ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onConfirmDelete(event.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaCheck />
                      Confirmer
                    </button>

                    <button
                      type="button"
                      onClick={onCancelDelete}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      <FaTimes />
                      Annuler
                    </button>
                  </>
                ) : (
                  <>
                    <IconButton
                      title="Voir le détail"
                      icon={FaEye}
                      tone="blue"
                      onClick={() => onDetails(event)}
                    />

                    <IconButton
                      title="Modifier"
                      icon={FaPen}
                      tone="orange"
                      onClick={() => onEdit(event)}
                    />

                    <IconButton
                      title={
                        event.is_active
                          ? "Désactiver l'événement"
                          : "Activer l'événement"
                      }
                      icon={FaPowerOff}
                      tone={event.is_active ? "slate" : "emerald"}
                      onClick={() => onToggleActive(event.id)}
                    />

                    <IconButton
                      title={
                        event.can_register
                          ? "Fermer les inscriptions"
                          : "Ouvrir les inscriptions"
                      }
                      icon={event.can_register ? FaLock : FaLockOpen}
                      tone={event.can_register ? "rose" : "emerald"}
                      onClick={() => onToggleRegister(event.id)}
                    />

                    <IconButton
                      title="Supprimer"
                      icon={FaTrashAlt}
                      tone="rose"
                      onClick={() => onAskDelete(event.id)}
                    />
                  </>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}